import { useEffect, useState, useCallback } from 'react';
import {
  List,
  Grid,
  InputAdornment,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  FormControlLabel,
  Checkbox,
  Link,
  Box,
  Button,
  Divider,
} from '@mui/material';
import { Visibility, VisibilityOff, Close as CloseIcon } from '@mui/icons-material';
import { ErrorMessageBox, LinkStyled } from './loginStyles';
import { DialogProps, LoginFieldValues } from '../../../interfaces/interfaces';
import { ResetPassword } from '../resetPassword/resetPassword';
import { useTranslation } from 'react-i18next';
import { FacebookSignIn, GoogleSignIn } from '../../../controls';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { authenticate, setRememberMe } from '../../../redux/reducers/authReducer';
import { RootState } from '../../../redux/store';
import { AppleSignIn } from '../externalLogin/appleSignIn';
import { useRouteNavigator } from '../../../routes/useRouteNavigator';
import { AnonymousLogin } from './anonymousLogin';
import { AppStyle } from '../../../helpers/appStyle';
import { appStyleMode } from '../../../helpers/clientConfigs';
import { ButtonLoadingIndicator } from '../../../controls';

const inputValues = {
  loginName: '',
  password: '',
};

export function LoginDialog(props: DialogProps & { allowAnonymous?: boolean }) {
  const { onClose, open, allowAnonymous } = props;
  const routeNavigator = useRouteNavigator();
  const dispatch = useAppDispatch();
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [textFieldValues, setTextFieldValues] = useState<LoginFieldValues>(inputValues);
  const [showPsw, setShowPsw] = useState(false);
  const [showPswReset, setShowPswReset] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>(null);
  const [anonymousLogin, setAnonymousLogin] = useState(false);
  const rememberMe = useAppSelector((state: RootState) => state.authState.rememberMe);
  const isAuthenticating = useAppSelector((state: RootState) => state.authState.loading);

  const { t } = useTranslation();

  const handleClose = () => {
    onClose();
  };

  const handleFieldChange = (e: any) => {
    const { name, value } = e.target;
    const fieldValue = { [name]: value } as Pick<LoginFieldValues, keyof LoginFieldValues>;

    setTextFieldValues({
      ...textFieldValues,
      ...fieldValue,
    });
  };

  const login = async () => {
    if (isButtonDisabled || isAuthenticating) {
      return;
    }

    setIsButtonDisabled(true);

    dispatch(authenticate({ input: textFieldValues.loginName, password: textFieldValues.password }))
      .unwrap()
      .then(() => {
        setErrorMessage(null);
        setTextFieldValues(inputValues);
        setIsButtonDisabled(false);
        handleClose();
      })
      .catch((error) => {
        setIsButtonDisabled(false);
        onLoginFailure(error);
        return;
      });
  };

  const validate = useCallback(() => {
    if (Object.values(textFieldValues).every((value) => value !== '')) {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [textFieldValues]);

  useEffect(() => {
    validate();
  }, [validate]);

  useEffect(() => {
    if (open) {
      setAnonymousLogin(false);
    }
  }, [open]);

  const onLoginFailure = (err: string) => {
    setErrorMessage(err);
  };

  const openResetPassword = () => {
    handleClose();
    setShowPswReset(true);
  };

  const closeResetPassword = () => {
    setShowPswReset(false);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ py: '10px', pr: '50px' }}>
          {anonymousLogin ? t('anonymousLogin:loginAnonymous') : t('account:login')}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 0,
          }}
        >
          <CloseIcon htmlColor="black" />
        </IconButton>
        <DialogContent sx={{ pt: '0px' }}>
          <List>
            {!anonymousLogin && (
              <>
                <TextField
                  fullWidth
                  sx={{ mb: '10px' }}
                  slotProps={{
                    input: {
                      sx: {
                        borderRadius: '10px',
                      },
                    },
                  }}
                  name="loginName"
                  onChange={handleFieldChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isButtonDisabled && !isAuthenticating) {
                      e.preventDefault();
                      login();
                    }
                  }}
                  label={t('account:loginInput')}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  sx={{ my: '10px' }}
                  slotProps={{
                    input: {
                      sx: {
                        borderRadius: '10px',
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPsw(!showPsw)} edge="end">
                            {showPsw ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  name="password"
                  onChange={handleFieldChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isButtonDisabled && !isAuthenticating) {
                      e.preventDefault();
                      login();
                    }
                  }}
                  label={t('account:password')}
                  type={showPsw ? 'text' : 'password'}
                  variant="outlined"
                />
                {errorMessage && <ErrorMessageBox>{errorMessage}</ErrorMessageBox>}
                <Grid container sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <FormControlLabel
                    sx={{ pb: '10px' }}
                    control={<Checkbox checked={rememberMe} onChange={() => dispatch(setRememberMe(!rememberMe))} />}
                    label={<Typography sx={{ fontSize: '16px' }}>{t('account:rememberMe')}</Typography>}
                  />
                  <Link
                    color="secondary"
                    sx={{
                      fontSize: '16px',
                      '&:hover': {
                        cursor: 'pointer',
                      },
                      pb: '10px',
                    }}
                    underline="hover"
                    onClick={() => openResetPassword()}
                  >
                    {t('account:forgotPassword')}
                  </Link>
                </Grid>
                <Button
                  sx={{
                    width: '100%',
                    backgroundColor: 'button.secondary.background',
                    color: 'button.primary.color',
                    height: '50px',
                  }}
                  onClick={() => login()}
                  disabled={isButtonDisabled || isAuthenticating}
                  loading={isAuthenticating}
                  loadingIndicator={<ButtonLoadingIndicator />}
                  variant="contained"
                >
                  <Box>{t('account:login')}</Box>
                </Button>
                <Divider sx={{ fontSize: '18px', my: '10px' }}>{t('account:or')}</Divider>
                <Grid container spacing={2} sx={{ justifyContent: 'center', alignItems: 'center' }}>
                  <Grid>
                    <FacebookSignIn
                      isVisible={!isAuthenticating}
                      onSignedIn={() => handleClose()}
                      onError={(err) => onLoginFailure(err)}
                    />
                  </Grid>
                  <Grid>
                    <GoogleSignIn
                      isVisible={!isAuthenticating}
                      onSignedIn={() => handleClose()}
                      onError={(err) => onLoginFailure(err)}
                    />
                  </Grid>
                  <Grid>
                    <AppleSignIn
                      isVisible={!isAuthenticating}
                      onSignedIn={() => handleClose()}
                      onError={(err) => onLoginFailure(err)}
                    />
                  </Grid>
                </Grid>
                <Grid container sx={{ mt: '10px', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '18px' }}>{t('account:missingAccount')}</Typography>
                  <LinkStyled underline="hover" onClick={() => routeNavigator.goToRegistration()}>
                    {t('account:register')}
                  </LinkStyled>
                </Grid>
                {allowAnonymous && appStyleMode !== AppStyle.Mobile && (
                  <Grid container sx={{ mt: '10px', justifyContent: 'center', alignItems: 'center' }}>
                    <LinkStyled underline="hover" onClick={() => allowAnonymous && setAnonymousLogin(true)}>
                      {t('anonymousLogin:loginAnonymous')}
                    </LinkStyled>
                  </Grid>
                )}
              </>
            )}
            {allowAnonymous && anonymousLogin && <AnonymousLogin onLogin={handleClose} />}
          </List>
        </DialogContent>
      </Dialog>
      <ResetPassword open={showPswReset} onClose={closeResetPassword} />
    </>
  );
}
